/**
 * CORE-1364 — shared SignalR connection options that keep the JWT out of the
 * browser console and out of the negotiate URL.
 *
 * Two leaks lived in every hub builder of this repo:
 *
 *  1. No builder configured a logger, so `HttpConnection` fell back to
 *     `ConsoleLogger(LogLevel.Information)` and `WebSocketTransport` printed
 *     "WebSocket connected to wss://…/hubs/chat?id=…&access_token=<jwt>" on
 *     every connect — the live user token (sub, email, role, exp) on the
 *     console, reproduced on the home page without opening a chat.
 *     `hubLogger` keeps the same levels as SignalR's default logger (so the
 *     connect/disconnect diagnostics stay) but runs every line through
 *     `redactUrlSecrets` first — the same scrub CORE-1059 applies to Sentry.
 *
 *  2. Several hooks appended `?access_token=${jwt}` to the hub URL by hand.
 *     That put the token into the negotiate POST URL as well. With
 *     `accessTokenFactory` SignalR sends negotiate with an `Authorization`
 *     header and only appends the query token to the WebSocket upgrade,
 *     where the browser leaves it no other choice.
 *
 * The logger is passed through `IHttpConnectionOptions.logger` rather than
 * `configureLogging()` on purpose: it is the transport logger that leaks, and
 * `HubConnectionBuilder.build()` honours an options-level logger as-is.
 */
import * as signalR from '@microsoft/signalr';
import { redactUrlSecrets } from './sentryScrub';
import { withHubTicket } from './hubTicket';

/** SignalR logger that never prints a bearer token. Same level → console
 *  method mapping as SignalR's `ConsoleLogger(Information)`: Trace/Debug are
 *  dropped, Information → info, Warning → warn, Error/Critical → error. */
export const hubLogger: signalR.ILogger = {
  log(logLevel: signalR.LogLevel, message: string): void {
    let sink: (msg: string) => void;
    let level: string;
    switch (logLevel) {
      case signalR.LogLevel.Critical:
        sink = console.error; level = 'Critical'; break;
      case signalR.LogLevel.Error:
        sink = console.error; level = 'Error'; break;
      case signalR.LogLevel.Warning:
        sink = console.warn; level = 'Warning'; break;
      case signalR.LogLevel.Information:
        sink = console.info; level = 'Information'; break;
      default:
        return;
    }
    sink(`[${new Date().toISOString()}] ${level}: ${redactUrlSecrets(message)}`);
  },
};

/**
 * Options for `HubConnectionBuilder.withUrl(url, …)`: the redacting logger,
 * plus an `accessTokenFactory` when the caller has a token. Guests (no token)
 * get no factory at all, so SignalR sends no empty `Bearer` header.
 * CORE-1736 — the factory hands out a hub ticket, not the JWT (`hubTicket.ts`).
 */
export function hubConnectionOptions(
  authToken?: string | null,
): signalR.IHttpConnectionOptions {
  return authToken
    ? { logger: hubLogger, accessTokenFactory: withHubTicket(() => authToken) }
    : { logger: hubLogger };
}
